import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Pagination,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddShoppingCart,
  ImageNotSupported,
  Inventory2Outlined,
} from '@mui/icons-material';
import axios from 'axios';
import { apiHost } from '../../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

const getFotoUrl = (foto, baseUrl) => {
  if (!foto) return null;
  if (foto.startsWith('http')) return foto;
  return `${baseUrl}/uploads/${foto}`;
};

const ProductCard = ({ item, baseUrl, onAdd, loading }) => {
  const { Barang, stok } = item;
  const fotoUrl = getFotoUrl(Barang?.foto, baseUrl);
  const [imgError, setImgError] = useState(false);
  const harga = Number(Barang?.harga || 0);
  const isHabis = stok <= 0;

  return (
    <Box
      sx={{
        border: '0.5px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        opacity: isHabis ? 0.55 : 1,
        '&:hover': isHabis
          ? {}
          : {
              boxShadow: '0 2px 12px 0 rgba(0,0,0,0.09)',
              borderColor: 'primary.main',
            },
      }}
    >
      <Box
        sx={{
          width: '100%',
          aspectRatio: '4/3',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {fotoUrl && !imgError ? (
          <Box
            component="img"
            src={fotoUrl}
            alt={Barang?.namabarang}
            onError={() => setImgError(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <ImageNotSupported sx={{ fontSize: 36, color: 'grey.400' }} />
        )}
        {isHabis && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Chip label="Stok Habis" size="small" color="error" />
          </Box>
        )}
        {Barang?.Kategori?.namakategori && (
          <Chip
            label={Barang.Kategori.namakategori}
            size="small"
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              fontSize: 10,
              height: 20,
              bgcolor: 'rgba(255,255,255,0.88)',
              color: 'text.secondary',
              border: '0.5px solid',
              borderColor: 'divider',
            }}
          />
        )}
      </Box>

      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {Barang?.namabarang || '-'}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', letterSpacing: 0.3 }}>
          {Barang?.kodebarang || '-'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 0.75 }}>
          <Box>
            <Typography variant="body2" fontWeight={700} color="primary.main">
              Rp {harga.toLocaleString('id-ID')}
            </Typography>
            <Typography variant="caption" color={stok <= 5 && stok > 0 ? 'warning.main' : 'text.secondary'}>
              Stok: {stok}
            </Typography>
          </Box>

          <Tooltip title={isHabis ? 'Stok habis' : 'Tambah ke keranjang'} placement="top">
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={isHabis || loading}
                onClick={() => onAdd({ Barang, stok })}
                sx={{
                  border: '0.5px solid',
                  borderColor: 'primary.light',
                  borderRadius: 1.5,
                  '&:hover': { bgcolor: 'primary.50' },
                }}
              >
                {loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <AddShoppingCart sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

const ProductSkeleton = () => (
  <Box sx={{ border: '0.5px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
    <Skeleton variant="rectangular" sx={{ aspectRatio: '4/3', width: '100%' }} />
    <Box sx={{ p: 1.25 }}>
      <Skeleton width="80%" height={18} />
      <Skeleton width="50%" height={14} sx={{ mt: 0.5 }} />
      <Skeleton width="60%" height={22} sx={{ mt: 1 }} />
    </Box>
  </Box>
);

const ProductDisplayRetail = ({ baseUrl, onAddToCart, loadingKode = null, reloadTrigger = 0 }) => {
  const [page, setPage] = useState(1);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const LIMIT = 12;

  useEffect(() => {
    fetchProducts(page);
  }, [page, reloadTrigger]);

  const fetchProducts = async (currentPage) => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/barangcabang`, {
        withCredentials: true,
      });
      const data = res.data?.data || [];
      setResults(data);
      setTotal(data.length);
    } catch (err) {
      console.error('Error fetching products:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const paginatedResults = results.slice(
    (page - 1) * LIMIT,
    page * LIMIT
  );

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const bs3 = {
    panelDefault: {
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: 'none',
      mb: 2,
      bgcolor: '#fff',
    },
    panelHeading: (color = '#f5f5f5', textColor = '#333') => ({
      bgcolor: color,
      color: textColor,
      px: 2,
      py: 1.2,
      borderBottom: '1px solid #ddd',
      borderRadius: '4px 4px 0 0',
      fontWeight: 700,
      fontSize: '14px',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    }),
    text: {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: '13px',
      color: '#333',
    },
    badge: (bg = '#337ab7') => ({
      display: 'inline-block',
      bgcolor: bg,
      color: '#fff',
      fontSize: '11px',
      fontWeight: 700,
      px: 1,
      py: 0.2,
      borderRadius: '10px',
      lineHeight: 1.4,
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    }),
  };

  return (
    <Card variant="outlined" sx={{ ...bs3.panelDefault }}>
      <CardHeader
        title="Produk Retail"
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        subheader={
          !loading ? `${total} produk tersedia` : 'Memuat produk...'
        }
        subheaderTypographyProps={{ variant: 'caption' }}
        action={
          total > 0 && (
            <Chip
              icon={<Inventory2Outlined sx={{ fontSize: 14 }} />}
              label={`${total} item`}
              size="small"
              variant="outlined"
              sx={{ mr: 1, mt: 1 }}
            />
          )
        }
        sx={{ pb: 1 }}
      />

      <CardContent sx={{ pt: 0 }}>
        {loading ? (
          <Grid container spacing={1.5}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <ProductSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : paginatedResults.length > 0 ? (
          <>
            <Grid container spacing={1.5}>
              {paginatedResults.map((item) => (
                <Grid
                  item
                  xs={6}
                  sm={4}
                  md={3}
                  key={`${item.baranguuid}-${item.cabanguuid}`}
                >
                  <ProductCard
                    item={item}
                    baseUrl={baseUrl}
                    onAdd={onAddToCart}
                    loading={loading}
                  />
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  size="small"
                  color="primary"
                  shape="rounded"
                />
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              py: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              color: 'text.secondary',
            }}
          >
            <Inventory2Outlined sx={{ fontSize: 40, opacity: 0.4 }} />
            <Typography variant="body2">Tidak ada produk tersedia</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductDisplayRetail;